import { isEqual } from 'lodash';
import qs from 'qs';
import { useEffect, useRef } from 'react';
import { useFilters } from '../../hooks/index.js';
import { actions } from '../../state/index.js';
/**
 * creates a location as string that can be used in history.pushSate or history.replaceState
 * @param location Location like object
 * @returns a string representing the combination of pathname
 */
const createHref = (location) => {
    const pathname = location.pathname;
    const search = location.search;
    const hash = location.hash;
    let path = pathname || '/';
    if (search && search !== '?')
        path += search.charAt(0) === '?' ? search : '?' + search;
    if (hash && hash !== '#')
        path += hash.charAt(0) === '#' ? hash : '#' + hash;
    return path;
};
/**
 * push the Location like object to the history
 * @param path Location like object
 */
const historyPush = (path) => {
    const href = createHref(path);
    window.history.pushState(null, '', href);
    window.dispatchEvent(new PopStateEvent('popstate'));
};
/**
 * replace the current location with the provided Location like object
 * @param path Location like object
 */
const historyReplace = (path) => {
    const href = createHref(path);
    window.history.replaceState(null, '', href);
    window.dispatchEvent(new PopStateEvent('popstate'));
};
/**
 *
 * @returns a Location like object from the window.location one
 */
const getLocation = () => {
    const loc = window.location;
    return {
        pathname: loc.pathname,
        search: loc.search,
        hash: loc.hash
    };
};
export const useHashParams = (name, enableQueryParams, normalizedFilterConfig, deserialize = (queryParams) => queryParams, serialize = (queryParams) => queryParams) => {
    const location = getLocation();
    const previousFilters = useRef();
    const previousHash = useRef();
    const { dispatch, filters, initialized } = useFilters(name);
    const originalHash = qs.parse(location.hash.replace('#', ''));
    const currentHash = deserialize(originalHash);
    const deserializedHash = Object.entries(currentHash).reduce((acc, [filterName, filterValue]) => {
        acc[filterName] = normalizedFilterConfig[filterName]?.deserialize?.(filterValue);
        return acc;
    }, {});
    useEffect(() => {
        if (!enableQueryParams || !initialized)
            return;
        const filtersAndHashMatch = isEqual(deserializedHash, filters);
        const hashChangedByFilterBar = !isEqual(originalHash, currentHash);
        // Compare reference and not values
        const filtersChanged = filters !== previousFilters.current;
        const hashChanged = !isEqual(deserializedHash, previousHash.current);
        const initialReplaceComplete = previousFilters.current;
        if (!filtersAndHashMatch || hashChangedByFilterBar) {
            const serializedFilters = Object.keys(filters).reduce((acc, filterName) => {
                acc[filterName] = normalizedFilterConfig[filterName]?.serialize?.(filters[filterName]);
                return acc;
            }, {});
            const newLocation = {
                ...location,
                hash: `#${qs.stringify(serialize(serializedFilters), {
                    arrayFormat: 'brackets'
                })}`
            };
            if (filtersChanged) {
                initialReplaceComplete ? historyPush(newLocation) : historyReplace(newLocation);
            }
            else if (hashChanged) {
                // Hash can change from going back in browser history, or clicking on a link to the current page with no params set
                // istanbul ignore next
                if (Object.keys(deserializedHash).length === 0) {
                    historyReplace(newLocation);
                }
                else {
                    dispatch({
                        type: actions.replaceFilters,
                        payload: {
                            name: name,
                            filters: deserializedHash
                        }
                    });
                }
            }
        }
        previousFilters.current = filters;
        previousHash.current = deserializedHash;
    }, [filters, location.hash, enableQueryParams]);
    return enableQueryParams ? deserializedHash : {};
};
//# sourceMappingURL=useHashParams.js.map